export type TemplateSourceFilter = 'all' | 'system' | 'custom';
export type TemplateConfidenceScore = 'high' | 'medium' | 'low' | string;
export type TemplateArchiveReason =
  | 'no_longer_needed'
  | 'superseded'
  | 'jurisdiction_change'
  | 'firm_policy'
  | 'duplicate'
  | 'other';

export interface TemplateJurisdiction {
  level?: 'federal' | 'state' | 'county' | 'municipal' | string;
  state_code?: string;
  county_fips?: string;
  court_name?: string;
}

export interface TemplateForm {
  form_number: string;
  form_name: string;
  issuing_body?: string;
  url?: string;
  is_mandatory: boolean;
  last_revised?: string;
  notes?: string;
}

export interface TemplateTask {
  title: string;
  description?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent' | string;
  due_date_offset_days?: number;
  assignee_role?: string;
  tags?: string[];
  required_forms?: TemplateForm[];
  is_court_mandated?: boolean;
  mandate_reference?: string;
  statutory_deadline?: {
    reference?: string;
    calculation?: string;
  };
  estimated_cost?: number;
  estimated_hours?: number;
}

export interface TemplatePhase {
  id?: string;
  name: string;
  description?: string;
  order: number;
  tasks: TemplateTask[];
}

export interface TaskTemplateRecord {
  id: string;
  realm_id: string;
  name: string;
  description?: string;
  practice_area?: string;
  jurisdiction_code?: string;
  template_family_key?: string;
  template_kind?: 'base' | 'jurisdiction_base' | 'jurisdiction_variant' | 'firm_variant' | string;
  variant_slug?: string;
  lifecycle_status?: 'draft' | 'validated' | 'stale' | string;
  inherits_updates?: boolean;
  tasks: TemplateTask[];
  jurisdiction?: TemplateJurisdiction;
  case_subtype?: string;
  parent_template_id?: string;
  phases?: TemplatePhase[];
  estimated_duration_days?: number;
  complexity_level?: 'simple' | 'moderate' | 'complex' | string;
  statutory_references?: string[];
  last_crawl_date?: string;
  crawl_job_id?: string;
  confidence_score?: TemplateConfidenceScore;
  is_active: boolean;
  is_system_template: boolean;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
  // Archive fields
  is_archived?: boolean;
  archived_date?: string;
  archived_by_user_id?: string;
  archive_reason?: TemplateArchiveReason;
  archive_notes?: string;
  superseded_by_template_id?: string;
  // Revision tracking
  revision_number?: number;
  last_edit_reason?: string;
  last_edited_by_user_id?: string;
}

// ─── Edit Impact / Audit Types ───

export type TemplateChangeCategory = 'court_rule_update' | 'form_change' | 'task_restructure' | 'correction' | 'other';

export const CHANGE_CATEGORY_LABELS: Record<TemplateChangeCategory, string> = {
  court_rule_update: 'Court Rule Update',
  form_change: 'Form Change',
  task_restructure: 'Task Restructure',
  correction: 'Correction',
  other: 'Other',
};

export interface EditImpactPreview {
  template_id: string;
  template_name: string;
  revision_number: number;
  last_edited_by_user_id?: string;
  last_edit_reason?: string;
  updated_at?: string;
  inheriting_variants: Array<{
    id: string;
    name: string;
    case_subtype?: string;
    template_kind: string;
    usage_count: number;
  }>;
  independent_variants: Array<{
    id: string;
    name: string;
    template_kind: string;
  }>;
  active_matter_count: number;
  safe_to_edit: boolean;
}

export interface TemplateAuditLogEntry {
  id: string;
  realm_id: string;
  template_id: string;
  template_name?: string;
  action: string;
  actor_user_id?: string;
  actor_name?: string;
  change_reason?: string;
  change_category?: string;
  previous_state?: Record<string, unknown>;
  new_state?: Record<string, unknown>;
  field_changes?: Record<string, { old: unknown; new: unknown }>;
  affected_variant_count: number;
  affected_variant_ids: string[];
  metadata?: Record<string, unknown>;
  created_at: string;
}

// ─── Archive Types ───

export interface ArchiveTaskTemplatePayload {
  archive_reason: TemplateArchiveReason;
  archive_notes?: string;
  cascade_to_variants?: boolean;
  superseded_by_template_id?: string;
}

export interface TemplateDependencyMatter {
  matter_id: string;
  matter_number: string;
  active_task_count: number;
}

export interface TemplateDependencyChild {
  id: string;
  name: string;
  template_kind: string;
  usage_count: number;
  is_archived: boolean;
}

export interface TemplateDependencyCheckResult {
  template_id: string;
  template_name: string;
  is_system_template: boolean;
  active_matter_count: number;
  matters: TemplateDependencyMatter[];
  child_variants: TemplateDependencyChild[];
  can_archive: boolean;
  can_hard_delete: boolean;
}

export interface ArchiveTaskTemplateResult {
  template: TaskTemplateRecord;
  cascaded_variants: Array<{ id: string; name: string }>;
}

export interface ArchivedTemplateListFilters {
  archive_reason?: string;
  template_kind?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export const ARCHIVE_REASON_LABELS: Record<string, string> = {
  no_longer_needed: 'No Longer Needed',
  superseded: 'Superseded',
  jurisdiction_change: 'Jurisdiction Change',
  firm_policy: 'Firm Policy',
  duplicate: 'Duplicate',
  other: 'Other',
};

export const ARCHIVE_REASON_COLORS: Record<string, string> = {
  no_longer_needed: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  superseded: 'bg-lex-accent-soft text-lex-accent dark:bg-lex-accent-soft dark:text-lex-accent',
  jurisdiction_change: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-200',
  firm_policy: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-200',
  duplicate: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-200',
  other: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
};

export interface TaskTemplateListItem extends TaskTemplateRecord {
  usage_count: number;
  task_count: number;
  phase_count: number;
  form_count: number;
  catalog_form_count: number;
}

export interface TaskTemplatePagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface TaskTemplateListResponse {
  templates: TaskTemplateListItem[];
  pagination: TaskTemplatePagination;
}

export interface TaskTemplateInheritanceNode {
  id: string;
  name: string;
  parent_template_id?: string;
}

export interface TaskTemplateDetailResponse {
  template: TaskTemplateRecord;
  resolved_template: TaskTemplateRecord;
  inheritance_chain: TaskTemplateInheritanceNode[];
  usage_count: number;
  task_count: number;
  form_count: number;
  catalog_form_count: number;
  phase_count: number;
}

export interface TaskTemplateValidationConflict {
  task_index: number;
  task_title: string;
  conflict_type: 'deadline_violation' | 'mandatory_task_removed' | 'mandatory_form_removed' | string;
  description: string;
  statute_reference: string;
  severity: 'error' | 'warning';
  suggestion: string;
}

export interface TaskTemplateValidationWarning {
  type: 'stale_data' | 'low_confidence' | 'missing_forms' | string;
  description: string;
}

export interface TaskTemplateValidationResult {
  valid: boolean;
  conflicts: TaskTemplateValidationConflict[];
  warnings: TaskTemplateValidationWarning[];
}

export interface TaskTemplateListFilters {
  practice_area?: string;
  jurisdiction?: string;
  source?: TemplateSourceFilter;
  search?: string;
  page?: number;
  pageSize?: number;
}

const ADMIN_TEMPLATE_API_BASE = '/api/v1/task-templates/admin';
const COURT_INTELLIGENCE_API_BASE = '/api/v1/court-intelligence';

export type ManualCourtExtractionTool = 'forms' | 'judges' | 'fees';

export interface CourtIntelligenceRunSummary {
  id: string;
  tool_id: string;
  run_type: string;
  status: string;
  created_at: string;
  completed_at: string | null;
  warning_count: number;
  confidence_score: number | null;
}

export interface CourtIntelligenceStatusResponse {
  pipeline_active: boolean;
  jurisdiction_code: string | null;
  last_run: CourtIntelligenceRunSummary | null;
  pending_review_count: number;
  recent_runs: CourtIntelligenceRunSummary[];
}

export interface CourtReviewQueueItem {
  id: string;
  jurisdiction_code: string;
  item_type: string;
  status: 'pending' | 'in_review' | 'approved' | 'rejected' | 'dismissed' | string;
  severity: 'info' | 'warning' | 'error' | string;
  title: string;
  description: string | null;
  proposed_changes: Record<string, unknown> | null;
  source_refs: string[];
  run_id: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  resolution_notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface CourtReviewQueueListResponse {
  items: CourtReviewQueueItem[];
  total: number;
}

export interface CourtReviewQueueActionResponse {
  item: CourtReviewQueueItem;
  updated_registry?: {
    jurisdiction_code: string;
    lifecycle_status: string;
    validated_by: string | null;
    validated_at: string | null;
    stale_detected_at: string | null;
  };
  updated_data_rows?: number;
  updated_endpoint_rows?: number;
  updated_form_rows?: number;
}

export interface TriggerManualCourtExtractionPayload {
  jurisdiction_code?: string;
  practice_area?: string;
  court_name?: string;
  division?: string;
  form_source_url?: string;
  judge_source_url?: string;
  fee_source_url?: string;
  tools?: ManualCourtExtractionTool[];
  no_cache?: boolean;
}

export interface TriggerToolSummary {
  tool_id: string;
  success: boolean;
  processing_time_ms?: number;
  warning_count: number;
  error?: string | null;
}

export interface TriggerManualCourtExtractionResult {
  started_at: string;
  finished_at: string;
  realm_id: string;
  jurisdiction_code: string;
  no_cache: boolean;
  tools_requested: ManualCourtExtractionTool[];
  cache_invalidated?: Record<string, number>;
  results: TriggerToolSummary[];
}

export interface SynthesizeCourtTemplatePayload {
  jurisdiction_code: string;
  practice_area: string;
  case_subtype?: string;
  variant_slug?: string;
  template_name?: string;
  base_template_id?: string;
  inherits_updates?: boolean;
}

export interface SynthesizeCourtTemplateResult {
  template: TaskTemplateRecord;
  base_template: { id: string; name: string } | null;
  was_updated: boolean;
  evidence: {
    forms_total: number;
    fees_total: number;
    judges_total: number;
    confidence_score: 'high' | 'medium' | 'low';
    lifecycle_status: 'draft' | 'validated' | 'stale';
  };
}

export interface CourtWizardDiscoverPayload {
  jurisdiction_code: string;
  court_name: string;
  root_url: string;
  practice_area?: string;
}

export interface CourtWizardEndpointProbe {
  endpoint_type: 'forms' | 'judges' | 'fees' | 'rules';
  url: string;
  status_code: number | null;
  http_ok: boolean;
  content_type: string | null;
  title: string | null;
  source: 'profile_confirmed' | 'profile_candidate' | 'heuristic' | 'search';
  score: number;
  reasons: string[];
  error?: string | null;
}

export interface CourtWizardDiscoverResult {
  jurisdiction_code: string;
  court_name: string;
  root_url: string;
  profile_match: {
    found: boolean;
    profile_path: string | null;
    lifecycle_status: string | null;
    confirmed_endpoints: Record<string, string | null>;
  };
  probes: {
    forms: CourtWizardEndpointProbe[];
    judges: CourtWizardEndpointProbe[];
    fees: CourtWizardEndpointProbe[];
    rules: CourtWizardEndpointProbe[];
  };
  recommended: {
    forms_url: string | null;
    judges_url: string | null;
    fees_url: string | null;
    rules_url: string | null;
  };
  readiness: {
    can_run_full_extraction: boolean;
    missing_required: string[];
  };
  next_actions: string[];
}

export interface CourtWizardTriggerPayload {
  jurisdiction_code: string;
  court_name: string;
  root_url: string;
  practice_area: string;
  tools?: ManualCourtExtractionTool[];
  endpoints: {
    forms_url?: string;
    judges_url?: string;
    fees_url?: string;
    rules_url?: string;
  };
  no_cache?: boolean;
}

export interface CourtWizardTriggerResult {
  operation_id: string;
  status: 'queued' | 'running' | 'completed' | 'partial_failed' | 'failed';
  started_at: string;
  progress_endpoint: string;
  logs_endpoint: string;
}

export interface CourtWizardStepState {
  tool: ManualCourtExtractionTool;
  tool_id: string;
  requested_endpoint: string | null;
  status: 'queued' | 'running' | 'completed' | 'failed';
  started_at: string | null;
  completed_at: string | null;
  run_id: string | null;
  records_extracted: number | null;
  warning_count: number;
  warnings: string[];
  error: string | null;
  likely_causes: string[];
  suggested_fixes: string[];
}

export interface CourtWizardProgressResult {
  operation_id: string;
  realm_id: string;
  user_id: string;
  jurisdiction_code: string;
  court_name: string;
  root_url: string;
  practice_area: string;
  tools_requested: ManualCourtExtractionTool[];
  status: 'queued' | 'running' | 'completed' | 'partial_failed' | 'failed';
  started_at: string;
  updated_at: string;
  completed_at: string | null;
  no_cache: boolean;
  steps: CourtWizardStepState[];
  completion_percent: number;
  success_count: number;
  failure_count: number;
  warning_count: number;
}

export interface CourtWizardLogsResult {
  operation_id: string;
  jurisdiction_code: string;
  court_name: string;
  status: 'queued' | 'running' | 'completed' | 'partial_failed' | 'failed';
  started_at: string;
  completed_at: string | null;
  summary: {
    success_count: number;
    failure_count: number;
    warning_count: number;
    extracted_record_total: number;
  };
  failures: Array<{
    tool_id: string;
    error: string | null;
    likely_causes: string[];
    suggested_fixes: string[];
    requested_endpoint: string | null;
  }>;
  recommended_actions: string[];
  export_text: string;
}

export interface CourtWizardEndpointOption {
  url: string;
  label: string;
  source: 'registry_primary' | 'registry_endpoint' | 'profile_confirmed' | 'profile_candidate' | 'heuristic';
  is_recommended: boolean;
  lifecycle_status?: string | null;
  format_family?: string | null;
  http_status?: number | null;
}

export interface CourtWizardOptionsResult {
  selected: {
    jurisdiction_code: string | null;
    court_registry_id: string | null;
    court_name: string | null;
    root_url: string | null;
    lifecycle_status: string | null;
  };
  jurisdictions: Array<{
    jurisdiction_code: string;
    state_code: string | null;
    county_fips: string | null;
    label: string;
    court_count: number;
    form_count: number;
  }>;
  courts: Array<{
    id: string;
    jurisdiction_code: string;
    court_name: string;
    base_url: string | null;
    court_type: string | null;
    lifecycle_status: string;
  }>;
  endpoint_options: {
    forms_url: CourtWizardEndpointOption[];
    judges_url: CourtWizardEndpointOption[];
    fees_url: CourtWizardEndpointOption[];
    rules_url: CourtWizardEndpointOption[];
  };
  recommended: {
    forms_url: string | null;
    judges_url: string | null;
    fees_url: string | null;
    rules_url: string | null;
  };
  practice_area_options: Array<{
    value: string;
    label: string;
  }>;
}

function getAuthHeaders(): HeadersInit {
  const token = localStorage.getItem('webelieve_access_token');
  return {
    Authorization: token ? `Bearer ${token}` : '',
    'Content-Type': 'application/json',
  };
}

function buildNonJsonErrorMessage(status: number, text: string): string {
  const snippet = text.replace(/\s+/g, ' ').trim().slice(0, 140);
  if (snippet.toLowerCase().includes('<!doctype') || snippet.toLowerCase().includes('<html')) {
    return `Request failed (${status}). Server returned HTML instead of JSON; endpoint may be unavailable.`;
  }
  return `Request failed (${status}). ${snippet || 'No response body.'}`;
}

async function parseResponse<T>(response: Response): Promise<T> {
  const text = await response.text();
  const contentType = response.headers.get('content-type') || '';

  let payload: any = null;
  if (text) {
    if (contentType.includes('application/json')) {
      try {
        payload = JSON.parse(text);
      } catch {
        throw new Error(`Request failed (${response.status}). Invalid JSON response.`);
      }
    } else if (text.trim().startsWith('{') || text.trim().startsWith('[')) {
      try {
        payload = JSON.parse(text);
      } catch {
        payload = null;
      }
    }
  }

  if (!response.ok) {
    if (payload && typeof payload === 'object') {
      const message = payload?.description || payload?.message || `Request failed (${response.status})`;
      throw new Error(message);
    }
    throw new Error(buildNonJsonErrorMessage(response.status, text));
  }

  if (payload === null) return {} as T;
  return payload as T;
}

function toQuery(filters: TaskTemplateListFilters): string {
  const params = new URLSearchParams();
  if (filters.practice_area) params.set('practice_area', filters.practice_area);
  if (filters.jurisdiction) params.set('jurisdiction', filters.jurisdiction);
  if (filters.source && filters.source !== 'all') params.set('source', filters.source);
  if (filters.search) params.set('search', filters.search);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

export async function listTaskTemplatesAdmin(
  filters: TaskTemplateListFilters = {},
): Promise<TaskTemplateListResponse> {
  const response = await fetch(`${ADMIN_TEMPLATE_API_BASE}/list${toQuery(filters)}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (response.status === 404) {
    return listTaskTemplatesLegacy(filters);
  }
  return parseResponse<TaskTemplateListResponse>(response);
}

export async function getTaskTemplateAdminDetail(templateId: string): Promise<TaskTemplateDetailResponse> {
  const response = await fetch(`${ADMIN_TEMPLATE_API_BASE}/${templateId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (response.status === 404) {
    return getTaskTemplateLegacyDetail(templateId);
  }
  return parseResponse<TaskTemplateDetailResponse>(response);
}

export async function forkTaskTemplateAdmin(
  templateId: string,
  payload?: { name?: string; description?: string },
): Promise<TaskTemplateRecord> {
  const response = await fetch(`${ADMIN_TEMPLATE_API_BASE}/${templateId}/fork`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload || {}),
  });
  if (response.status === 404) {
    throw new Error('Template admin fork endpoint is unavailable. Deploy the latest backend routes first.');
  }
  const parsed = await parseResponse<{ template: TaskTemplateRecord }>(response);
  return parsed.template;
}

export async function updateTaskTemplateAdmin(
  templateId: string,
  payload: Partial<TaskTemplateRecord>,
): Promise<TaskTemplateRecord> {
  const response = await fetch(`${ADMIN_TEMPLATE_API_BASE}/${templateId}`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  if (response.status === 404) {
    throw new Error('Template admin update endpoint is unavailable. Deploy the latest backend routes first.');
  }
  const parsed = await parseResponse<{ template: TaskTemplateRecord }>(response);
  return parsed.template;
}

export async function validateTaskTemplateAdmin(
  templateId: string,
  payload?: Partial<TaskTemplateRecord>,
): Promise<TaskTemplateValidationResult> {
  const response = await fetch(`${ADMIN_TEMPLATE_API_BASE}/${templateId}/validate`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload || {}),
  });
  if (response.status === 404) {
    return {
      valid: true,
      conflicts: [],
      warnings: [
        {
          type: 'stale_data',
          description: 'Validation endpoint unavailable; only client-side checks were applied.',
        },
      ],
    };
  }
  return parseResponse<TaskTemplateValidationResult>(response);
}

export async function getCourtIntelligenceStatus(
  jurisdictionCode?: string,
): Promise<CourtIntelligenceStatusResponse> {
  const params = new URLSearchParams();
  if (jurisdictionCode) params.set('jurisdiction_code', jurisdictionCode);
  const query = params.toString();
  const response = await fetch(
    `${COURT_INTELLIGENCE_API_BASE}/status${query ? `?${query}` : ''}`,
    {
      method: 'GET',
      headers: getAuthHeaders(),
    },
  );

  if (response.status === 404) {
    return {
      pipeline_active: false,
      jurisdiction_code: jurisdictionCode || null,
      last_run: null,
      pending_review_count: 0,
      recent_runs: [],
    };
  }

  return parseResponse<CourtIntelligenceStatusResponse>(response);
}

export async function triggerCourtIntelligenceManualRun(
  payload: TriggerManualCourtExtractionPayload = {},
): Promise<TriggerManualCourtExtractionResult> {
  const response = await fetch(`${COURT_INTELLIGENCE_API_BASE}/trigger`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });

  if (response.status === 404) {
    throw new Error('Court intelligence trigger endpoint is unavailable. Deploy the latest backend routes first.');
  }

  return parseResponse<TriggerManualCourtExtractionResult>(response);
}

export async function getCourtIntelligenceReviewQueue(
  params: { jurisdiction_code?: string; status?: string; limit?: number } = {},
): Promise<CourtReviewQueueListResponse> {
  const query = new URLSearchParams();
  if (params.jurisdiction_code) query.set('jurisdiction_code', params.jurisdiction_code);
  if (params.status) query.set('status', params.status);
  if (typeof params.limit === 'number' && Number.isFinite(params.limit)) {
    query.set('limit', String(params.limit));
  }
  const queryString = query.toString();
  const response = await fetch(
    `${COURT_INTELLIGENCE_API_BASE}/review-queue${queryString ? `?${queryString}` : ''}`,
    {
      method: 'GET',
      headers: getAuthHeaders(),
    },
  );
  if (response.status === 404) {
    return { items: [], total: 0 };
  }
  return parseResponse<CourtReviewQueueListResponse>(response);
}

export async function approveCourtIntelligenceReviewItem(
  reviewItemId: string,
  notes?: string,
): Promise<CourtReviewQueueActionResponse> {
  const response = await fetch(`${COURT_INTELLIGENCE_API_BASE}/review-queue/${reviewItemId}/approve`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ notes: notes || '' }),
  });
  return parseResponse<CourtReviewQueueActionResponse>(response);
}

export async function rejectCourtIntelligenceReviewItem(
  reviewItemId: string,
  notes?: string,
): Promise<CourtReviewQueueActionResponse> {
  const response = await fetch(`${COURT_INTELLIGENCE_API_BASE}/review-queue/${reviewItemId}/reject`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ notes: notes || '' }),
  });
  return parseResponse<CourtReviewQueueActionResponse>(response);
}

export async function synthesizeTemplateFromCourtData(
  payload: SynthesizeCourtTemplatePayload,
): Promise<SynthesizeCourtTemplateResult> {
  const response = await fetch(`${ADMIN_TEMPLATE_API_BASE}/synthesize-from-court`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return parseResponse<SynthesizeCourtTemplateResult>(response);
}

export interface SynthesizeSubtypeTemplatesResult {
  master: SynthesizeCourtTemplateResult;
  subtypes: Array<SynthesizeCourtTemplateResult & { subtype_key: string }>;
}

export async function synthesizeSubtypeTemplates(
  payload: { jurisdiction_code: string; practice_area: string },
): Promise<SynthesizeSubtypeTemplatesResult> {
  const response = await fetch(`${ADMIN_TEMPLATE_API_BASE}/synthesize-subtypes`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return parseResponse<SynthesizeSubtypeTemplatesResult>(response);
}

export async function createTaskTemplateAdmin(
  payload: Partial<TaskTemplateRecord> & { name: string },
): Promise<TaskTemplateRecord> {
  const response = await fetch(`${ADMIN_TEMPLATE_API_BASE}`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  const parsed = await parseResponse<{ template: TaskTemplateRecord }>(response);
  return parsed.template;
}

// ─── Archive API Functions ───

export async function getTemplateDependencies(templateId: string): Promise<TemplateDependencyCheckResult> {
  const response = await fetch(`${ADMIN_TEMPLATE_API_BASE}/${templateId}/dependencies`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  const parsed = await parseResponse<{ success: boolean; data: TemplateDependencyCheckResult }>(response);
  return parsed.data;
}

export async function archiveTaskTemplate(
  templateId: string,
  payload: ArchiveTaskTemplatePayload,
): Promise<ArchiveTaskTemplateResult> {
  const response = await fetch(`${ADMIN_TEMPLATE_API_BASE}/${templateId}/archive`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  const parsed = await parseResponse<{ success: boolean; data: ArchiveTaskTemplateResult }>(response);
  return parsed.data;
}

export async function restoreTaskTemplate(templateId: string): Promise<TaskTemplateRecord> {
  const response = await fetch(`${ADMIN_TEMPLATE_API_BASE}/${templateId}/restore`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({}),
  });
  const parsed = await parseResponse<{ success: boolean; data: TaskTemplateRecord }>(response);
  return parsed.data;
}

export async function hardDeleteTaskTemplate(templateId: string): Promise<void> {
  const response = await fetch(`${ADMIN_TEMPLATE_API_BASE}/${templateId}`, {
    method: 'DELETE',
    headers: getAuthHeaders(),
  });
  await parseResponse<{ success: boolean }>(response);
}

export async function listArchivedTaskTemplates(
  filters: ArchivedTemplateListFilters = {},
): Promise<TaskTemplateListResponse> {
  const params = new URLSearchParams();
  if (filters.archive_reason) params.set('archive_reason', filters.archive_reason);
  if (filters.template_kind) params.set('template_kind', filters.template_kind);
  if (filters.search) params.set('search', filters.search);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.pageSize) params.set('pageSize', String(filters.pageSize));
  const query = params.toString();
  const response = await fetch(
    `${ADMIN_TEMPLATE_API_BASE}/archived/list${query ? `?${query}` : ''}`,
    { method: 'GET', headers: getAuthHeaders() },
  );
  return parseResponse<TaskTemplateListResponse>(response);
}

// ─── Edit Impact / Audit API Functions ───

export async function getEditImpactPreview(templateId: string): Promise<EditImpactPreview> {
  const response = await fetch(`${ADMIN_TEMPLATE_API_BASE}/${templateId}/impact-preview`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  const parsed = await parseResponse<{ success: boolean; data: EditImpactPreview }>(response);
  return parsed.data;
}

export async function getAuditHistory(
  templateId: string,
  options?: { limit?: number; offset?: number },
): Promise<{ entries: TemplateAuditLogEntry[]; total: number }> {
  const params = new URLSearchParams();
  if (options?.limit) params.set('limit', String(options.limit));
  if (options?.offset) params.set('offset', String(options.offset));
  const query = params.toString();
  const response = await fetch(
    `${ADMIN_TEMPLATE_API_BASE}/${templateId}/audit-history${query ? `?${query}` : ''}`,
    { method: 'GET', headers: getAuthHeaders() },
  );
  const parsed = await parseResponse<{ success: boolean; data: { entries: TemplateAuditLogEntry[]; total: number } }>(response);
  return parsed.data;
}

export async function getAuditSnapshot(auditLogId: string): Promise<TemplateAuditLogEntry> {
  const response = await fetch(`${ADMIN_TEMPLATE_API_BASE}/audit/${auditLogId}/snapshot`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  const parsed = await parseResponse<{ success: boolean; data: TemplateAuditLogEntry }>(response);
  return parsed.data;
}

export async function rollbackTemplate(
  templateId: string,
  auditLogId: string,
): Promise<TaskTemplateRecord> {
  const response = await fetch(`${ADMIN_TEMPLATE_API_BASE}/${templateId}/rollback`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({ audit_log_id: auditLogId }),
  });
  const parsed = await parseResponse<{ success: boolean; data: TaskTemplateRecord }>(response);
  return parsed.data;
}

export interface SubtypeInfo {
  key: string;
  label: string;
  description: string;
  form_count: number;
  template_id: string | null;
  template_status: string | null;
}

export async function listSubtypesForPracticeArea(
  practiceArea: string,
  jurisdictionCode?: string,
): Promise<{ subtypes: SubtypeInfo[] }> {
  const params = new URLSearchParams();
  params.set('practice_area', practiceArea);
  if (jurisdictionCode) params.set('jurisdiction_code', jurisdictionCode);
  const response = await fetch(`/api/v1/task-templates/subtypes?${params.toString()}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  if (response.status === 404) {
    return { subtypes: [] };
  }
  return parseResponse<{ subtypes: SubtypeInfo[] }>(response);
}

export async function discoverCourtIntelligenceWizard(
  payload: CourtWizardDiscoverPayload,
): Promise<CourtWizardDiscoverResult> {
  const response = await fetch(`${COURT_INTELLIGENCE_API_BASE}/wizard/discover`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return parseResponse<CourtWizardDiscoverResult>(response);
}

export async function getCourtIntelligenceWizardOptions(
  params: { jurisdiction_code?: string; court_registry_id?: string } = {},
): Promise<CourtWizardOptionsResult> {
  const query = new URLSearchParams();
  if (params.jurisdiction_code) query.set('jurisdiction_code', params.jurisdiction_code);
  if (params.court_registry_id) query.set('court_registry_id', params.court_registry_id);
  const queryString = query.toString();
  const response = await fetch(
    `${COURT_INTELLIGENCE_API_BASE}/wizard/options${queryString ? `?${queryString}` : ''}`,
    {
      method: 'GET',
      headers: getAuthHeaders(),
    },
  );
  return parseResponse<CourtWizardOptionsResult>(response);
}

export async function triggerCourtIntelligenceWizard(
  payload: CourtWizardTriggerPayload,
): Promise<CourtWizardTriggerResult> {
  const response = await fetch(`${COURT_INTELLIGENCE_API_BASE}/wizard/trigger`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(payload),
  });
  return parseResponse<CourtWizardTriggerResult>(response);
}

export async function getCourtIntelligenceWizardProgress(
  operationId: string,
): Promise<CourtWizardProgressResult> {
  const response = await fetch(`${COURT_INTELLIGENCE_API_BASE}/wizard/progress/${operationId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return parseResponse<CourtWizardProgressResult>(response);
}

export async function getCourtIntelligenceWizardLogs(
  operationId: string,
): Promise<CourtWizardLogsResult> {
  const response = await fetch(`${COURT_INTELLIGENCE_API_BASE}/wizard/logs/${operationId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  return parseResponse<CourtWizardLogsResult>(response);
}

function toListItem(template: TaskTemplateRecord): TaskTemplateListItem {
  const phases = normalizeTemplatePhases(template);
  const taskCount = phases.reduce((total, phase) => total + phase.tasks.length, 0);
  return {
    ...template,
    usage_count: 0,
    task_count: taskCount,
    phase_count: phases.length,
    form_count: countTemplateForms(template),
    catalog_form_count: 0,
  };
}

function applyClientFilters(
  templates: TaskTemplateRecord[],
  filters: TaskTemplateListFilters,
): TaskTemplateRecord[] {
  const search = (filters.search || '').trim().toLowerCase();
  const practiceArea = (filters.practice_area || '').trim().toLowerCase().replace(/\s+/g, '_');
  const jurisdiction = (filters.jurisdiction || '').trim().toLowerCase();
  const source = filters.source || 'all';

  return templates.filter((template) => {
    if (practiceArea && (template.practice_area || '').toLowerCase().replace(/\s+/g, '_') !== practiceArea) return false;
    if (source === 'system' && !template.is_system_template) return false;
    if (source === 'custom' && template.is_system_template) return false;

    if (jurisdiction) {
      const jurisdictionText = JSON.stringify(template.jurisdiction || {}).toLowerCase();
      const jurisdictionCode = String(template.jurisdiction_code || '').trim().toLowerCase();
      const matchesCode =
        jurisdictionCode === jurisdiction ||
        (jurisdiction.length === 2 && jurisdictionCode.startsWith(`${jurisdiction}-`));
      if (!matchesCode && !jurisdictionText.includes(jurisdiction)) return false;
    }

    if (search) {
      const haystack = `${template.name} ${template.description || ''} ${template.practice_area || ''}`.toLowerCase();
      if (!haystack.includes(search)) return false;
    }

    return true;
  });
}

async function listTaskTemplatesLegacy(filters: TaskTemplateListFilters): Promise<TaskTemplateListResponse> {
  const response = await fetch('/api/v1/task-templates', {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  const parsed = await parseResponse<{ templates?: TaskTemplateRecord[] } | TaskTemplateRecord[]>(response);
  const sourceTemplates = Array.isArray(parsed) ? parsed : Array.isArray(parsed.templates) ? parsed.templates : [];
  const filtered = applyClientFilters(sourceTemplates, filters).map(toListItem);

  const pageSize = Math.min(100, Math.max(1, Number(filters.pageSize || 20)));
  const page = Math.max(1, Number(filters.page || 1));
  const total = filtered.length;
  const totalPages = total === 0 ? 0 : Math.ceil(total / pageSize);
  const start = (page - 1) * pageSize;
  const templates = filtered.slice(start, start + pageSize);

  return {
    templates,
    pagination: {
      page,
      pageSize,
      total,
      totalPages,
    },
  };
}

async function getTaskTemplateLegacyDetail(templateId: string): Promise<TaskTemplateDetailResponse> {
  const response = await fetch(`/api/v1/task-templates/${templateId}`, {
    method: 'GET',
    headers: getAuthHeaders(),
  });
  const parsed = await parseResponse<{ template?: TaskTemplateRecord } | TaskTemplateRecord>(response);
  const template = (parsed as { template?: TaskTemplateRecord }).template
    ? (parsed as { template: TaskTemplateRecord }).template
    : (parsed as TaskTemplateRecord);

  const phases = normalizeTemplatePhases(template);
  const taskCount = phases.reduce((sum, p) => sum + p.tasks.length, 0);
  return {
    template,
    resolved_template: template,
    inheritance_chain: [
      {
        id: template.id,
        name: template.name,
        parent_template_id: template.parent_template_id,
      },
    ],
    usage_count: 0,
    task_count: taskCount,
    form_count: countTemplateForms(template),
    catalog_form_count: 0,
    phase_count: phases.length,
  };
}

export function normalizeTemplatePhases(template?: Pick<TaskTemplateRecord, 'phases' | 'tasks'>): TemplatePhase[] {
  if (!template) return [];
  if (Array.isArray(template.phases) && template.phases.length > 0) {
    return [...template.phases]
      .map((phase, index) => ({
        ...phase,
        order: typeof phase.order === 'number' ? phase.order : index + 1,
        tasks: Array.isArray(phase.tasks) ? phase.tasks : [],
      }))
      .sort((a, b) => a.order - b.order);
  }
  if (Array.isArray(template.tasks) && template.tasks.length > 0) {
    return [
      {
        name: 'Default Phase',
        order: 1,
        tasks: template.tasks,
      },
    ];
  }
  return [];
}

export function countTemplateForms(template?: Pick<TaskTemplateRecord, 'phases' | 'tasks'>): number {
  const phases = normalizeTemplatePhases(template);
  const formKeys = new Set<string>();
  for (const phase of phases) {
    for (const task of phase.tasks) {
      for (const form of task.required_forms || []) {
        const key = `${(form.form_number || '').trim().toLowerCase()}|${(form.form_name || '').trim().toLowerCase()}`;
        if (key !== '|') formKeys.add(key);
      }
    }
  }
  return formKeys.size;
}

export function formatJurisdictionLabel(jurisdiction?: TemplateJurisdiction): string {
  if (!jurisdiction) return 'Unspecified';
  const level = (jurisdiction.level || '').toLowerCase();
  if (level === 'federal') return 'Federal';
  if (level === 'state') return jurisdiction.state_code || 'State';
  if (level === 'county') {
    if (jurisdiction.state_code && jurisdiction.county_fips) {
      return `${jurisdiction.state_code} - ${jurisdiction.county_fips}`;
    }
    return jurisdiction.court_name || 'County';
  }
  if (level === 'municipal') return jurisdiction.court_name || 'Municipal';
  return jurisdiction.court_name || 'Unspecified';
}
